import { useState, useEffect } from "react";
import { getPosts, getAllPosts, getLineByPost } from "@service/post";
import { AxiosError } from "axios";
import { type PostWithLines, type PostoInfo } from "./Interfaces";
import { popupError, popupSuccess } from "@service/zendesk";
import { useNavigate } from "react-router-dom";
import { isCNPJ } from "@utils/validateCNPJ";

export const usePosts = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const [postSearched, setPostSearched] = useState<PostWithLines[]>([]);
  const [allPosts, setAllPosts] = useState<PostoInfo[]>([]);

  const [showPost, setShowPost] = useState<boolean>(false);

  const [state, setState] = useState<string>("");
  const [city, setCity] = useState<string>("");
  const [line, setLine] = useState<string>("");
  const [cnpj, setCnpj] = useState<string>("");
  const [type, setType] = useState<string>("");
  const [status, setStatus] = useState<string>("");
  const [code, setCode] = useState<string>("");
  const [name, setName] = useState<string>("");

  useEffect(() => {
    const fetchAllPosts = async () => {
      setLoading(true);
      try {
        const posts = await getAllPosts();
        setAllPosts(posts);
      } catch {
        popupError("Erro ao carregar a lista de postos.", false);
      } finally {
        setLoading(false);
      }
    };

    fetchAllPosts();
  }, []);

  const handleSearch = async () => {
    if (!cnpj && !state && !city && !line && !type && !status && !name && !code) {
      popupError("Informe ao menos um filtro.", false);
      return;
    }

    if (cnpj && !isCNPJ(cnpj)) {
      popupError("CNPJ inválido", false);
      return;
    }

    setLoading(true);

    try {
      let results: PostWithLines[] = [];
      if ((!cnpj && (code || name)) || cnpj) {
        const postosFiltrados = cnpj
          ? allPosts.filter((p) => p.cnpj.replace(/\D/g, "") === cnpj.replace(/\D/g, ""))
          : allPosts.filter((p) => {
              const matchName = name ? p.nome?.toLowerCase().includes(name.toLowerCase()) : true;
              const matchCode = code ? p.codigo_posto?.toLowerCase() === code.toLowerCase() : true;
              return matchName && matchCode;
            });

        if (postosFiltrados.length === 0) {
          setPostSearched([]);
          setShowPost(false);
          popupError("Nenhum posto encontrado.", false);
          return;
        }

        const apiResults = await Promise.all(
          postosFiltrados.map(async (post) => {
            try {
              const response = await getPosts(post.cnpj.replace(/\D/g, ""));
              if (response.status === 200 && response.data.results.length > 0) {
                const postoData = response.data.results[0] as PostWithLines;
                try {
                  const linesResponse = await getLineByPost(post.cnpj.replace(/\D/g, ""));
                  return { ...postoData, lines: linesResponse };
                } catch {
                  return { ...postoData, lines: [] };
                }
              }
              return null;
            } catch {
              return null;
            }
          })
        );

        results = apiResults.filter((r): r is PostWithLines & { lines: string[] } => r !== null);
      }

      if (!cnpj && !code && !name) {
        const filteredPosts = allPosts.filter((post) => {
          const matchState = state ? post.estado?.toLowerCase() === state.toLowerCase() : true;
          const matchCity = city ? post.cidade?.toLowerCase().includes(city.toLowerCase()) : true;
          return matchState && matchCity;
        });

        if (filteredPosts.length === 0) {
          setPostSearched([]);
          popupError("Nenhum posto encontrado.", false);
          return;
        }

        const apiResults = await Promise.all(
          filteredPosts.map(async (post) => {
            try {
              const response = await getPosts(post.cnpj.replace(/\D/g, ""));
              if (response.status === 200 && response.data.results.length > 0) {
                const postoData = response.data.results[0] as PostWithLines;
                try {
                  const linesResponse = await getLineByPost(post.cnpj.replace(/\D/g, ""));
                  return { ...postoData, lines: linesResponse };
                } catch {
                  return { ...postoData, lines: [] };
                }
              }
              return null;
            } catch {
              return null;
            }
          })
        );

        results = apiResults.filter((r): r is PostWithLines & { lines: string[] } => r !== null);
        results = results.filter((post) => {
          const matchType = type ? post.tipo_posto?.toLowerCase() === type.toLowerCase() : true;
          const matchStatus = status
            ? post.credenciamento?.toLowerCase() === status.toLowerCase()
            : true;
          const matchLine = line
            ? post.lines?.some((l) => l.linha?.toLowerCase().includes(line.toLowerCase()))
            : true;
          return matchType && matchStatus && matchLine;
        });
      }

      if (results.length > 0) {
        setPostSearched(results);
        popupSuccess(
          results.length > 1 ? "Postos encontrados com sucesso." : "Posto encontrado com sucesso.",
          false
        );
        setShowPost(true);
      } else {
        setPostSearched([]);
        setShowPost(false);
        popupError("Nenhum posto encontrado.", false);
      }
    } catch (err) {
      setPostSearched([]);
      const error = err as AxiosError;
      if (error.status === 404 || error.status === 400) {
        popupError("Nenhum posto encontrado.", false);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleState = (state: string) => {
    setState(state);
  };
  const handleCity = (city: string) => {
    setCity(city);
  };
  const handleLine = (line: string) => {
    setLine(line);
  };

  const handleCnpj = (cnpj: string) => {
    setCnpj(cnpj);
  };

  const handleType = (type: string) => {
    setType(type);
  };

  const handleStatus = (status: string) => {
    setStatus(status);
  };

  const handleName = (name: string) => {
    setName(name);
  };

  const handleCode = (code: string) => {
    setCode(code);
  };

  const clean = () => {
    setCnpj("");
    setState("");
    setStatus("");
    setLine("");
    setType("");
    setCity("");
  };

  const backToHome = () => navigate("/");

  const backToSearch = () => {
    setShowPost(false);
  };

  const LINE_LABELS: Record<string, string> = {
    ACE: "Acessórios",
    AQC: "Aquecedor de gás",
    RAD: "Auto Rádio",
    BRA: "Branca",
    BRP: "Branca Premium",
    COK: "Cooktop",
    "E/E": "Eletroeletrônicos",
    ELE: "Eletro Portátil",
    FER: "Ferramentas Elétricas",
    INF: "Informática",
    LCD: "Televisores e monitores",
    MQC: "Máquina de costura",
    NTB: "Notebooks",
    REFR: "Refrigeração",
    SPL: "Split",
    "A/V": "Audio e Vídeo",
    TEL: "Telefonia",
    PNE: "Pneus",
  };

  const getLineLabel = (linha: string): string => {
    const key = Object.keys(LINE_LABELS).find((k) => linha.includes(k));
    return key ? LINE_LABELS[key] : linha;
  };

  return {
    loading,
    postSearched,
    backToHome,

    handleState,
    state,
    handleCity,
    city,
    handleLine,
    line,
    handleCnpj,
    cnpj,
    handleSearch,
    handleType,
    type,
    handleStatus,
    status,
    clean,
    showPost,
    backToSearch,
    handleCode,
    code,
    handleName,
    name,
    getLineLabel,
  };
};
